import { z } from "zod"

export const CursorPositionSchema = z.object({
  x: z.number(),
  y: z.number(),
  name: z.string().default("Support"),
})

const ScrollAction = z.object({
  type: z.literal("scroll"),
  scope: z.enum(["window", "element"]),
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
  scrollTop: z.number(),
  scrollLeft: z.number(),
  timestamp: z.number(),
})

const AnnotationPoint = z.object({
  x: z.number(),
  y: z.number(),
})

const AnnotationStroke = z.object({
  type: z.literal("stroke"),
  points: z.array(AnnotationPoint).min(2),
  color: z.string(),
  width: z.number(),
  timestamp: z.number(),
})

const AnnotationClear = z.object({
  type: z.literal("clear"),
  timestamp: z.number(),
})

export const RemoteControlActionSchema = z.discriminatedUnion("type", [
  ScrollAction,
])

export const RemoteControlAnnotationSchema = z.discriminatedUnion("type", [
  AnnotationStroke,
  AnnotationClear,
])

export type RemoteControlAction = z.infer<typeof RemoteControlActionSchema>
export type RemoteControlAnnotation = z.infer<
  typeof RemoteControlAnnotationSchema
>

export type CursorPosition = z.infer<typeof CursorPositionSchema>

export interface VoiceboxToolDefinition<
  TSchema extends z.ZodTypeAny = z.ZodTypeAny,
> {
  description: string
  inputSchema: TSchema
  execute: (input: z.infer<TSchema>) => Promise<string> | string
}

export type VoiceboxTools = Record<string, VoiceboxToolDefinition>

export interface ToolCallEvent {
  toolId: string
  toolArgs: unknown
  response: string
}

export type ToolCallbacks = Record<string, (event: ToolCallEvent) => void>

export function tool<TSchema extends z.ZodTypeAny>(
  definition: VoiceboxToolDefinition<TSchema>
): VoiceboxToolDefinition<TSchema> {
  return definition
}

export interface VoiceboxProps {
  children?: React.ReactNode
  baseUrl?: string
  publishableKey?: string
  firstMessage?: string
  identifier?: string
  instructions?: string
  metadata?: Record<string, string>
  onError?: (error: string) => void
  tools?: VoiceboxTools
  sampleQuestions?: string[]
  shaderColor?: `#${string}`
  /** Internal: per-tool UI callbacks registered via `useTool` when `onToolCall` is passed. */
  toolCallbacks?: ToolCallbacks
}
